import { useState, useCallback, useEffect } from "react"
import { apiShort } from "api"
import { useRouter } from "next/router"
import { SHORT_CONFIG, SHORT_TYPES } from "constants/short"

export default function useShortData() {
  const [listShort, setListShort] = useState([])
  const [pageNum, setPageNum] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const router = useRouter()
  const { id, search } = router.query

  const getShortList = useCallback(async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      const { data } = await apiShort.getRandomShort({
        page_num: page,
        page_size: SHORT_CONFIG.PAGE_SIZE
      })
      if (data) {
        setListShort((prev) => (page === 1 ? data : [...prev, ...data]))
      }
    } catch (err) {
      console.error("Error fetching shorts:", err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  const getShortDetail = useCallback(
    async (shortId, page = 1, reload = false) => {
      try {
        setLoading(true)
        setError(null)
        let data = null

        if (search) {
          const res = await apiShort.getShortById({
            search: search || "",
            page_num: page,
            page_size: SHORT_CONFIG.PAGE_SIZE,
            short_id: shortId
          })
          data = res.data
        } else {
          const res = await apiShort.getShortRelated({
            id: shortId,
            type: SHORT_TYPES.RELATED
          })
          data = res.data
        }

        if (data) {
          if (reload) {
            const idx = listShort.findIndex((item) => item.id === shortId)
            if (idx !== -1) {
              setListShort([...listShort.slice(0, idx + 1), ...data])
            } else {
              setListShort(data)
            }
          } else {
            setListShort((prev) => (page === 1 ? data : [...prev, ...data]))
          }
        }
      } catch (err) {
        console.error("Error fetching shorts:", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    },
    [search, listShort]
  )

  const loadMore = useCallback(() => {
    if (loading || id) return
    const nextPage = pageNum + 1
    setPageNum(nextPage)
    if (id) {
      getShortDetail(id, nextPage)
    } else {
      getShortList(nextPage)
    }
  }, [pageNum, loading, id, getShortDetail, getShortList])

  const reload = useCallback(
    (shortId) => {
      getShortDetail(shortId, 1, true)
    },
    [getShortDetail]
  )

  // Initial load
  useEffect(() => {
    if (!router.isReady) return
    if (id) {
      getShortDetail(id, 1)
    } else {
      getShortList(1)
    }
  }, [id, router.isReady])

  return {
    listShort,
    loading,
    error,
    loadMore,
    reload
  }
}
